// 15.E ч.2 — оплата тарифа. Два пути:
//   1) QR через СБП (Точка) — мгновенно, тариф активируется автоматически;
//   2) по счёту для ЮРЛИЦ — пакет документов для проверки службой безопасности
//      + заявка на счёт/договор координатору (уходит в Telegram, 15.C/15.D).
// ВАЖНО: путь (2) НЕ активирует тариф — только после фактической оплаты.
import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileDown, Receipt, QrCode, ArrowLeft } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/Modal';

interface PayResp {
  qrc_id: string; payload: string | null; image: string | null;
  image_media_type: string; amount_rub: number; tariff: string;
}
interface StatusResp { paid: boolean; status: string }

// Основной продукт — первым и «рекомендуем»: сопровождение стоит «по цене двух
// прогонов», разовый прогон — пробник, который ведёт в него (PMF_NARRATIVE §6).
const TARIFFS = [
  { key: 'full', name: 'Доведение до экспертизы', price: 39000, recommended: true,
    desc: 'По цене двух прогонов: безлимит проверок и редакций, сравнение версий ПД — ведём проект до готовности.' },
  { key: 'express', name: 'Разовый прогон', price: 19500, recommended: false,
    desc: 'Пробный: по одному прогону каждой проверки. Без цикла редакций и сравнения версий. В течение месяца засчитывается при переходе на сопровождение.' },
];

// Реквизиты — дословно из описи пакета документов (BR-5: не переписывать по памяти).
const REQUISITES = 'ООО «Рейл Проджект Профэшналз» · ОГРН 1206600036829 · ИНН 6658535817 · КПП 665801001';

export function PaymentModal({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [view, setView] = useState<'menu' | 'invoice'>('menu');
  const [qr, setQr] = useState<PayResp | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);
  // Путь «по счёту»
  const [invTariff, setInvTariff] = useState('full');
  const [invNote, setInvNote] = useState('');
  const [invSending, setInvSending] = useState(false);
  // Выставленный счёт по проекту (адресный документ, отдаётся под логином
  // и только участнику проекта — см. GET /projects/{id}/invoice).
  const [invoice, setInvoice] = useState<{ filename: string; size: number } | null>(null);
  useEffect(() => {
    api.get<{ exists: boolean; filename?: string; size?: number }>(`/projects/${projectId}/invoice/meta`)
      .then((r) => setInvoice(r.exists ? { filename: r.filename || 'Счёт на оплату.pdf', size: r.size || 0 } : null))
      .catch(() => setInvoice(null));
  }, [projectId]);
  const [invSent, setInvSent] = useState(false);
  const [invErr, setInvErr] = useState<string | null>(null);

  const start = async (tariff: string) => {
    setBusy(true); setErr(null);
    try {
      setQr(await api.post<PayResp>(`/projects/${projectId}/payment`, { tariff }));
    } catch {
      setErr('Не удалось создать платёж. Попробуйте позже или обратитесь в поддержку.');
    } finally {
      setBusy(false);
    }
  };

  const requestInvoice = async () => {
    setInvSending(true); setInvErr(null);
    const t = TARIFFS.find((x) => x.key === invTariff);
    const question =
      `Запрос счёта и договора (оплата по безналичному расчёту).\n` +
      `Тариф: ${t?.name} — ${t?.price.toLocaleString('ru-RU')} ₽.\n` +
      (invNote.trim() ? `Реквизиты / комментарий заказчика:\n${invNote.trim()}` : 'Реквизиты заказчик вышлет отдельно.');
    try {
      await api.post(`/projects/${projectId}/consultations`, { question, attachments: [] });
      setInvSent(true);
    } catch {
      setInvErr('Не удалось отправить заявку. Попробуйте ещё раз или напишите координатору.');
    } finally {
      setInvSending(false);
    }
  };

  // Поллинг статуса каждые 4 с; при оплате — активируем тариф (инвалидация проекта).
  useEffect(() => {
    if (!qr || paid) return;
    const id = window.setInterval(async () => {
      try {
        const s = await api.get<StatusResp>(`/projects/${projectId}/payment/${qr.qrc_id}`);
        if (s.paid) {
          setPaid(true);
          qc.invalidateQueries({ queryKey: ['project', projectId] });
        }
      } catch { /* сеть/бэк — просто ждём следующий тик */ }
    }, 4000);
    return () => window.clearInterval(id);
  }, [qr, paid, projectId, qc]);

  return (
    <Modal title="Оплата тарифа" onClose={onClose} maxWidth={480}>
      {paid ? (
        <div style={{ textAlign: 'center', padding: '10px 0 4px' }}>
          <CheckCircle2 size={44} color="#10B981" />
          <h4 className="font-heading text-ink" style={{ fontSize: 17, fontWeight: 700, margin: '12px 0 4px' }}>
            Оплата прошла
          </h4>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 18 }}>
            Тариф активирован — можно запускать проверки.
          </p>
          <button type="button" onClick={onClose}
            style={{ padding: '9px 22px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                     background: '#0A1628', color: '#fff', border: 'none', cursor: 'pointer' }}>
            Готово
          </button>
        </div>
      ) : qr ? (
        <div style={{ textAlign: 'center' }}>
          <p className="text-ink" style={{ fontSize: 14, marginBottom: 12 }}>
            {TARIFFS.find((t) => t.key === qr.tariff)?.name} — <b>{qr.amount_rub.toLocaleString('ru-RU')} ₽</b>
          </p>
          {qr.image ? (
            <img src={`data:${qr.image_media_type};base64,${qr.image}`} alt="QR-код для оплаты по СБП"
                 style={{ width: 260, height: 260, margin: '0 auto', display: 'block' }} />
          ) : qr.payload ? (
            <a href={qr.payload} target="_blank" rel="noreferrer" style={{ color: '#2563EB', fontSize: 13 }}>
              Открыть ссылку для оплаты по СБП
            </a>
          ) : null}
          <p className="text-muted" style={{ fontSize: 12.5, lineHeight: 1.5, margin: '14px 0 6px' }}>
            Отсканируйте QR в банк-приложении и подтвердите оплату. Окно можно не закрывать —
            тариф активируется автоматически.
          </p>
          <p className="text-muted" style={{ fontSize: 12 }}>Ожидаем оплату…</p>
        </div>
      ) : view === 'invoice' ? (
        /* ── Путь 2: оплата по счёту (юрлицам) ── */
        invSent ? (
          <div style={{ textAlign: 'center', padding: '10px 0 4px' }}>
            <CheckCircle2 size={40} color="#10B981" />
            <h4 className="font-heading text-ink" style={{ fontSize: 16, fontWeight: 700, margin: '12px 0 4px' }}>
              Заявка отправлена
            </h4>
            <p className="text-muted" style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 16 }}>
              Координатор подготовит счёт и договор и свяжется с вами. Тариф активируется
              после поступления оплаты.
            </p>
            <button type="button" onClick={onClose}
              style={{ padding: '9px 22px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                       background: '#0A1628', color: '#fff', border: 'none', cursor: 'pointer' }}>
              Готово
            </button>
          </div>
        ) : (
          <div>
            <button type="button" onClick={() => setView('menu')}
              className="inline-flex items-center text-muted"
              style={{ gap: 5, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12.5, padding: 0, marginBottom: 12 }}>
              <ArrowLeft size={14} /> К выбору способа оплаты
            </button>
            <p className="text-muted" style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 14 }}>
              Для оплаты по безналичному расчёту: скачайте пакет документов — его обычно
              запрашивает служба безопасности и бухгалтерия — и запросите счёт с договором.
            </p>

            <div className="text-muted uppercase tracking-wider" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', marginBottom: 7 }}>
              Тариф для счёта
            </div>
            {TARIFFS.map((t) => (
              <button key={t.key} type="button" onClick={() => setInvTariff(t.key)}
                style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 8,
                         padding: '10px 12px', borderRadius: 10,
                         border: invTariff === t.key ? '2px solid #0A1628' : '1px solid #E5E7EB',
                         background: invTariff === t.key ? '#F8FAFC' : '#fff', cursor: 'pointer' }}>
                <span className="text-ink" style={{ fontSize: 13.5, fontWeight: 600 }}>
                  {t.name} · {t.price.toLocaleString('ru-RU')} ₽
                </span>
              </button>
            ))}

            {/* Счёт уже выставлен — даём его сразу, до всех заявок. */}
            {invoice && (
              <>
                <a href={`/projects/${projectId}/invoice`}
                  className="inline-flex items-center rounded-btn"
                  style={{ gap: 8, width: '100%', boxSizing: 'border-box', justifyContent: 'center',
                           margin: '12px 0 6px', padding: '11px 16px', fontSize: 13.5, fontWeight: 700,
                           background: '#0A1628', color: '#fff', border: 'none',
                           textDecoration: 'none', cursor: 'pointer' }}>
                  <Receipt size={16} /> Скачать счёт на оплату
                </a>
                <p className="text-muted" style={{ fontSize: 11.5, lineHeight: 1.45, margin: '0 0 14px' }}>
                  {invoice.filename}{invoice.size ? ` · ${Math.round(invoice.size / 1024)} КБ` : ''}.
                  Счёт выставлен по вашему проекту; тариф активируется после поступления оплаты.
                </p>
              </>
            )}

            <a href="/legal/contract-package"
              className="inline-flex items-center rounded-btn"
              style={{ gap: 8, width: '100%', boxSizing: 'border-box', justifyContent: 'center',
                       margin: '12px 0 6px', padding: '11px 16px', fontSize: 13.5, fontWeight: 600,
                       background: '#fff', color: '#0A1628', border: '1px solid #0A1628',
                       textDecoration: 'none', cursor: 'pointer' }}>
              <FileDown size={16} /> Скачать документы для договора (ZIP, ~10 МБ)
            </a>
            <p className="text-muted" style={{ fontSize: 11.5, lineHeight: 1.45, margin: '0 0 14px' }}>
              Устав, листы записи ЕГРЮЛ, свидетельство ИНН, решения о полномочиях,
              карта партнёра. {REQUISITES}
            </p>

            <textarea
              value={invNote}
              onChange={(e) => setInvNote(e.target.value)}
              maxLength={4000}
              rows={3}
              placeholder="Реквизиты вашей организации для счёта (необязательно — можно выслать отдельно)"
              style={{ width: '100%', fontSize: 13, lineHeight: 1.5, padding: '10px 12px',
                       border: '1px solid #CBD5E1', borderRadius: 8, outline: 'none',
                       resize: 'vertical', boxSizing: 'border-box', marginBottom: 10 }}
            />
            <button type="button" onClick={requestInvoice} disabled={invSending}
              className="inline-flex items-center rounded-btn"
              style={{ gap: 8, width: '100%', boxSizing: 'border-box', justifyContent: 'center',
                       padding: '12px 16px', fontSize: 14, fontWeight: 700, border: 'none',
                       background: invSending ? '#F3F4F6' : '#0A1628',
                       color: invSending ? '#9CA3AF' : '#fff',
                       cursor: invSending ? 'default' : 'pointer' }}>
              <Receipt size={16} /> {invSending ? 'Отправляем…' : 'Запросить счёт и договор'}
            </button>
            {invErr && <p style={{ color: '#B91C1C', fontSize: 12.5, marginTop: 8 }}>{invErr}</p>}
            <p className="text-muted" style={{ fontSize: 11.5, lineHeight: 1.45, marginTop: 10 }}>
              Тариф активируется после поступления оплаты — документы и заявка его не открывают.
            </p>
          </div>
        )
      ) : (
        /* ── Меню: выбор способа ── */
        <div>
          <p className="text-muted" style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 14 }}>
            Выберите тариф. Оплата — по QR через СБП из любого банк-приложения; тариф
            активируется автоматически после оплаты.
          </p>
          {TARIFFS.map((t) => (
            <button key={t.key} type="button" onClick={() => start(t.key)} disabled={busy}
              style={{
                display: 'block', width: '100%', textAlign: 'left', marginBottom: 10,
                padding: '12px 14px', borderRadius: 12,
                border: t.recommended ? '2px solid #0A1628' : '1px solid #E5E7EB',
                background: t.recommended ? '#F8FAFC' : '#fff',
                cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className="text-ink" style={{ fontSize: t.recommended ? 15 : 13.5, fontWeight: 700 }}>
                  {t.name} · {t.price.toLocaleString('ru-RU')} ₽
                </span>
                {t.recommended && (
                  <span style={{ fontSize: 10.5, fontWeight: 700, color: '#fff', background: '#0A1628',
                                 padding: '2px 8px', borderRadius: 20 }}>РЕКОМЕНДУЕМ</span>
                )}
                <span className="text-muted inline-flex items-center" style={{ gap: 4, fontSize: 11, marginLeft: 'auto' }}>
                  <QrCode size={12} /> по QR
                </span>
              </div>
              <div className="text-muted" style={{ fontSize: 12, marginTop: 3, lineHeight: 1.45 }}>{t.desc}</div>
            </button>
          ))}
          {busy && <p className="text-muted" style={{ fontSize: 12 }}>Создаём платёж…</p>}
          {err && <p style={{ color: '#B91C1C', fontSize: 12.5, marginTop: 6 }}>{err}</p>}

          {/* Второй путь — юрлицам (безнал): документы + счёт. */}
          <div style={{ borderTop: '1px solid #E5E7EB', marginTop: 14, paddingTop: 14 }}>
            <button type="button" onClick={() => setView('invoice')}
              className="inline-flex items-center rounded-btn"
              style={{ gap: 8, width: '100%', boxSizing: 'border-box', justifyContent: 'center',
                       padding: '11px 16px', fontSize: 13.5, fontWeight: 600,
                       background: '#fff', color: '#0A1628', border: '1px solid #CBD5E1', cursor: 'pointer' }}>
              <Receipt size={16} /> Юридическим лицам — оплата по счёту
            </button>
            <p className="text-muted" style={{ fontSize: 11.5, lineHeight: 1.45, marginTop: 8, textAlign: 'center' }}>
              Договор, счёт и пакет документов для проверки контрагента
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
}
